import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatError } from "../lib/api";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import useTitle from "../lib/useTitle";
import { toast } from "sonner";
import { ArrowLeft, Eye, EyeOff } from "lucide-react";
import { ExpectationsFields, HeartbeatField, windowToPayload } from "../components/ExpectationsFields";

export default function NewCheck() {
  useTitle("New Check");
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [kind, setKind] = useState("http_json");
  // HTTP / JSON
  const [url, setUrl] = useState("");
  const [token, setToken] = useState("");
  const [jsonPath, setJsonPath] = useState("");
  const [newestKey, setNewestKey] = useState("");
  // Airtable
  const [baseId, setBaseId] = useState("");
  const [table, setTable] = useState("");
  const [pat, setPat] = useState("");
  const [view, setView] = useState("");
  // Postgres
  const [dsn, setDsn] = useState("");
  const [query, setQuery] = useState("");
  // Expectations + alerts
  const [minNew, setMinNew] = useState(1);
  const [mode, setMode] = useState("growth");
  const [required, setRequired] = useState("");
  const [nonEmpty, setNonEmpty] = useState("");
  const [slackWebhook, setSlackWebhook] = useState("");
  const [alertKind, setAlertKind] = useState("slack");
  const [retryBeforeAlert, setRetryBeforeAlert] = useState(true);
  const [heartbeatHours, setHeartbeatHours] = useState("");
  const [heartbeatWindow, setHeartbeatWindow] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [emailAlerts, setEmailAlerts] = useState(false);
  useEffect(() => {
    api.get("/meta").then(({ data }) => setEmailAlerts(!!data.email_alerts)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      let config;
      if (kind === "airtable") {
        config = {
          base_id: baseId.trim(),
          table: table.trim(),
          personal_access_token: pat.trim() || null,
          view: view.trim() || null,
        };
      } else if (kind === "postgres") {
        config = {
          dsn: dsn.trim(),
          query: query.trim(),
        };
      } else {
        config = {
          url,
          bearer_token: token || null,
          json_path: jsonPath || null,
          newest_key: newestKey.trim() || null,
        };
      }
      const payload = {
        name,
        connector_kind: kind,
        config,
        expectations: {
          min_new_records: Number(minNew) || 0,
          growth_mode: mode,
          required_fields: required.split(",").map((s) => s.trim()).filter(Boolean),
          non_empty_fields: nonEmpty.split(",").map((s) => s.trim()).filter(Boolean),
        },
        alert_channels: slackWebhook.trim() ? [{ kind: alertKind, target: slackWebhook.trim() }] : [],
        retry_before_alert: retryBeforeAlert,
        heartbeat_hours: heartbeatHours === "" ? null : Number(heartbeatHours),
        heartbeat_window: windowToPayload(heartbeatHours, heartbeatWindow),
      };
      const { data } = await api.post("/checks", payload);
      toast.success("Check created");
      nav(`/checks/${data.id}`);
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rp-page">
      <Nav />
      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-12">
        <Link to="/dashboard" className="rp-link text-sm inline-flex items-center gap-1.5 mb-6" data-testid="back-to-dashboard">
          <ArrowLeft size={14} /> Back to dashboard
        </Link>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mb-10">Create a Check</h1>

        <form onSubmit={submit} className="space-y-10" autoComplete="off">
          <Section title="Name">
            <label htmlFor="check-name" className="sr-only">Check name</label>
            <input id="check-name" required autoComplete="off" className="rp-input" placeholder="orders-sync" value={name} onChange={(e) => setName(e.target.value)} data-testid="check-name-input" />
          </Section>

          <Section title="Destination" subtitle="Pick the connector VerifyRuns should re-read after each run.">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              <ConnectorOption
                active={kind === "http_json"}
                onClick={() => setKind("http_json")}
                title="HTTP / JSON"
                sub="A complete JSON array of records"
                testid="connector-http-json"
              />
              <ConnectorOption
                active={kind === "airtable"}
                onClick={() => setKind("airtable")}
                title="Airtable"
                sub="A base + table you own"
                testid="connector-airtable"
              />
              <ConnectorOption
                active={kind === "postgres"}
                onClick={() => setKind("postgres")}
                title="Postgres"
                sub="A read-only SELECT query"
                testid="connector-postgres"
              />
            </div>

            {kind === "http_json" && (
              <div className="space-y-4">
                <Field id="check-url" label="GET URL" hint="Use a complete, stable result set. HTTP pagination is not followed; a fixed-size page cannot measure total growth.">
                  <input id="check-url" required type="url" autoComplete="off" className="rp-input font-mono" placeholder="https://api.example.com/v1/orders" value={url} onChange={(e) => setUrl(e.target.value)} data-testid="check-url-input" />
                </Field>
                <Field id="check-token" label="Bearer token" optional hint="Encrypted at rest; shown masked afterwards.">
                  <SecretInput id="check-token" placeholder="eyJhbGciOi…" value={token} onChange={setToken} testid="check-token-input" />
                </Field>
                <Field id="check-jsonpath" label="JSON path to the array" optional hint="Leave empty if the response body itself is an array.">
                  <input id="check-jsonpath" type="text" className="rp-input font-mono" placeholder="data.records" value={jsonPath} onChange={(e) => setJsonPath(e.target.value)} data-testid="check-jsonpath-input" />
                </Field>
                <Field id="check-newestkey" label="Newest-record key" optional hint="A field that grows with time, e.g. created_at. Without one, the last element counts as newest.">
                  <input id="check-newestkey" type="text" className="rp-input font-mono" placeholder="created_at" value={newestKey} onChange={(e) => setNewestKey(e.target.value)} data-testid="check-newestkey-input" />
                </Field>
              </div>
            )}

            {kind === "airtable" && (
              <div className="space-y-4">
                <Field id="check-base-id" label="Base ID">
                  <input id="check-base-id" required type="text" className="rp-input font-mono" placeholder="appXXXXXXXXXXXXXX" value={baseId} onChange={(e) => setBaseId(e.target.value)} data-testid="check-base-id-input" />
                </Field>
                <Field id="check-table" label="Table name">
                  <input id="check-table" required type="text" className="rp-input font-mono" placeholder="Orders" value={table} onChange={(e) => setTable(e.target.value)} data-testid="check-table-input" />
                </Field>
                <Field id="check-pat" label="Personal access token" hint="Encrypted at rest; shown masked afterwards.">
                  <SecretInput id="check-pat" required placeholder="pat…" value={pat} onChange={setPat} testid="check-pat-input" />
                </Field>
                <Field id="check-view" label="View name" optional>
                  <input id="check-view" type="text" className="rp-input font-mono" placeholder="Grid view" value={view} onChange={(e) => setView(e.target.value)} data-testid="check-view-input" />
                </Field>
                <p className="text-xs text-quiet leading-relaxed">
                  Counts are paged up to 4,000 records; above that, growth checks fail as incomplete. Use a stable table or view. Create a PAT at
                  <a className="rp-inline ml-1" href="https://airtable.com/create/tokens" target="_blank" rel="noreferrer">airtable.com/create/tokens</a>
                  &nbsp;with <span className="font-mono">data.records:read</span> for the base.
                </p>
              </div>
            )}

            {kind === "postgres" && (
              <div className="space-y-4">
                <Field id="check-dsn" label="Connection string" hint="Encrypted at rest; shown masked afterwards.">
                  <SecretInput id="check-dsn" required placeholder="postgres://user:pass@host:5432/dbname" value={dsn} onChange={setDsn} testid="check-dsn-input" />
                </Field>
                <Field id="check-query" label="Read-only query">
                  <textarea
                    id="check-query"
                    required
                    rows={3}
                    className="rp-input font-mono resize-y"
                    placeholder="SELECT id, email, created_at FROM orders ORDER BY created_at DESC"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    data-testid="check-query-input"
                  />
                </Field>
                <p className="text-xs text-quiet leading-relaxed">
                  Use a read-only role and a single SELECT or WITH query. VerifyRuns counts your full query and samples up to 100 rows. Leave LIMIT out of a growth query; it caps the count too. Order newest first. Hosted connections require a publicly trusted TLS certificate.
                </p>
              </div>
            )}
          </Section>

          <Section title="Expectations" subtitle="Choose what a successful observation must satisfy. The first read establishes a baseline; it cannot prove growth yet.">
            <ExpectationsFields
              mode={mode} setMode={setMode}
              minNew={minNew} setMinNew={setMinNew}
              required={required} setRequired={setRequired}
              nonEmpty={nonEmpty} setNonEmpty={setNonEmpty}
            />
          </Section>

          <Section title="Heartbeat" subtitle="Optional. Catch the workflow that never ran: if no run arrives within this many hours, VerifyRuns records a FAIL and alerts.">
            <HeartbeatField value={heartbeatHours} onChange={setHeartbeatHours} window={heartbeatWindow} onWindowChange={setHeartbeatWindow} />
          </Section>

          <Section title="Alert channel" subtitle="Recommended. Get notified on failure and recovery, then test delivery from the Check page.">
            <label htmlFor="check-alert-target" className="text-[11px] uppercase tracking-wider text-quiet block mb-2">{alertKind === "email" ? "Email address" : "Webhook URL"} <span className="normal-case tracking-normal text-quiet/80">(optional)</span></label>
            <div className="grid sm:grid-cols-[140px_1fr] gap-2">
              <select className="rp-input font-mono" aria-label="Alert channel kind" value={alertKind} onChange={(e) => setAlertKind(e.target.value)} data-testid="check-alert-kind">
                <option value="slack">Slack</option>
                <option value="discord">Discord</option>
                <option value="email" disabled={!emailAlerts}>{emailAlerts ? "Email" : "Email · upcoming"}</option>
              </select>
              <input
                id="check-alert-target"
                type={alertKind === "email" ? "email" : "url"}
                className="rp-input font-mono"
                placeholder={alertKind === "email" ? "ops@example.com" : alertKind === "discord" ? "https://discord.com/api/webhooks/..." : "https://hooks.slack.com/services/..."}
                value={slackWebhook}
                onChange={(e) => setSlackWebhook(e.target.value)}
                data-testid="check-slack-input"
              />
            </div>
            {!emailAlerts && <p className="text-xs text-quiet mt-2" data-testid="email-alerts-upcoming-hint">Email alerts are next. Use Slack or Discord for now.</p>}
            <p className="text-xs text-quiet mt-2" data-testid="new-check-alert-guidance">{slackWebhook.trim() ? "After creating the Check, send a test and confirm it arrives." : "Without a channel, failures appear only in VerifyRuns. You can add a channel later."}</p>
            <p className="text-xs text-quiet leading-relaxed mt-2">
              Stored encrypted; only the last 4 characters are shown afterwards.
            </p>
            <label className="flex items-center gap-2 mt-4 cursor-pointer select-none" data-testid="retry-toggle-label">
              <input
                type="checkbox"
                className="w-4 h-4 accent-emerald-500"
                checked={retryBeforeAlert}
                onChange={(e) => setRetryBeforeAlert(e.target.checked)}
                data-testid="check-retry-toggle"
              />
              <span className="text-sm text-zinc-300">Retry a failed read or assertion before alerting</span>
            </label>
            <p className="text-xs text-quiet mt-2">Retry is due after 30 seconds and runs on the next scheduler tick. Incomplete assertions and reported failures alert without a retry; establishing the initial baseline does not alert.</p>
          </Section>

          {error && <div className="text-sm text-red-400 border border-red-500/25 bg-red-500/5 rounded-md p-3" data-testid="new-check-error">{error}</div>}

          <div className="flex gap-3">
            <button type="submit" className="rp-btn-primary" disabled={busy} data-testid="create-check-submit">
              {busy ? "Creating…" : "Create Check"}
            </button>
            <Link to="/dashboard" className="rp-btn-ghost" data-testid="cancel-new-check">Cancel</Link>
          </div>
        </form>
      </div>
      <Footer />
    </div>
  );
}

function Field({ id, label, optional = false, hint, children }) {
  return (
    <div>
      <label htmlFor={id} className="text-[11px] uppercase tracking-wider text-quiet block mb-2">
        {label}
        {optional && <span className="normal-case tracking-normal text-quiet/80"> (optional)</span>}
      </label>
      {children}
      {hint && <p className="text-xs text-quiet leading-relaxed mt-1.5">{hint}</p>}
    </div>
  );
}

function SecretInput({ id, value, onChange, placeholder, required = false, testid }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input
        id={id}
        required={required}
        type={show ? "text" : "password"}
        autoComplete="new-password"
        data-lpignore="true"
        data-1p-ignore="true"
        className="rp-input font-mono pr-11"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      />
      <button
        type="button"
        onClick={() => setShow((v) => !v)}
        className="absolute right-0 top-0 h-full w-11 inline-flex items-center justify-center text-quiet hover:text-zinc-200"
        aria-label={show ? "Hide value" : "Show value"}
        aria-pressed={show}
        data-testid={`${testid}-reveal`}
      >
        {show ? <EyeOff size={15} /> : <Eye size={15} />}
      </button>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div>
      <h2 className="font-display text-lg mb-1">{title}</h2>
      {subtitle && <p className="text-sm text-quiet mb-4">{subtitle}</p>}
      {children}
    </div>
  );
}

function ConnectorOption({ active, onClick, title, sub, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-left p-4 rounded-lg border transition-colors ${active ? "border-emerald-500/50 bg-emerald-500/5" : "border-hairline bg-ink hover:border-hairline-hover"}`}
    >
      <p className={`font-display text-sm mb-1 ${active ? "text-emerald-300" : "text-zinc-200"}`}>{title}</p>
      <p className="text-xs text-quiet">{sub}</p>
    </button>
  );
}
